<template>
  <BaseModal 
    :show="show"
    size="md" 
    :title="isEditing ? 'Editar Regla' : 'Nueva Regla'"
    :show-actions="true"
    :is-editing="isEditing"
    :loading="saving"
    @close="$emit('close')"
    @save="$emit('save')"
  >
    <form
      class="space-y-6"
      @submit.prevent="$emit('save')"
    >
      <!-- Basic Fields -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Nombre de la Regla *
        </label>
        <input
          v-model="currentRule.name"
          type="text"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Ej: Envío gratis en compras mayores a $500"
        >
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Descripción
        </label>
        <textarea
          v-model="currentRule.description"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          placeholder="Describe cómo funciona esta regla..."
        />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tipo de Regla *
          </label>
          <select
            v-model="currentRule.rule_type"
            required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            @change="$emit('rule-type-change')"
          >
            <option value="">
              Selecciona un tipo...
            </option>
            <option value="order_total_threshold">
              Umbral de Total de Orden
            </option>
            <option value="day_of_week">
              Día de la Semana
            </option>
            <option value="flat_rate">
              Tarifa Plana
            </option>
            <option value="compound_and">
              Regla Combinada (Y)
            </option>
            <option value="compound_or">
              Regla Combinada (O)
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Prioridad *
          </label>
          <input
            v-model.number="currentRule.priority"
            type="number"
            min="1"
            max="1000"
            required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder="100"
          >
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Menor número = mayor prioridad
          </p>
        </div>
      </div>

      <div class="flex items-center">
        <input
          id="is_active"
          v-model="currentRule.is_active"
          type="checkbox"
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
        >
        <label
          for="is_active"
          class="ml-2 block text-sm text-gray-900 dark:text-white"
        >
          Regla activa
        </label>
      </div>

      <!-- Rule Configuration -->
      <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 class="text-base font-medium text-gray-900 dark:text-white mb-4">
          Configuración de la Regla
        </h4>

        <!-- Order Total Threshold -->
        <RuleConfigOrderTotalThreshold 
          v-if="currentRule.rule_type === 'order_total_threshold'"
          v-model="currentRule.rule_config"
        />

        <!-- Day of Week -->
        <RuleConfigDayOfWeek 
          v-else-if="currentRule.rule_type === 'day_of_week'"
          v-model="currentRule.rule_config"
        />

        <!-- Flat Rate -->
        <RuleConfigFlatRate 
          v-else-if="currentRule.rule_type === 'flat_rate'"
          v-model="currentRule.rule_config"
        />

        <!-- Compound AND Rule -->
        <RuleConfigCompound
          v-else-if="currentRule.rule_type === 'compound_and'"
          v-model="currentRule.rule_config"
          operator="and"
          @add-condition="$emit('add-condition')"
          @remove-condition="$emit('remove-condition', $event)"
        />

        <!-- Compound OR Rule -->
        <RuleConfigCompound
          v-else-if="currentRule.rule_type === 'compound_or'"
          v-model="currentRule.rule_config"
          operator="or"
          @add-condition="$emit('add-condition')"
          @remove-condition="$emit('remove-condition', $event)"
        />

        <div
          v-else
          class="text-center py-8 text-gray-500 dark:text-gray-400"
        >
          Selecciona un tipo de regla para ver la configuración
        </div>
      </div>
    </form>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseModal from '@/components/common/BaseModal.vue'
import RuleConfigOrderTotalThreshold from './RuleConfigOrderTotalThreshold.vue'
import RuleConfigDayOfWeek from './RuleConfigDayOfWeek.vue'
import RuleConfigFlatRate from './RuleConfigFlatRate.vue'
import RuleConfigCompound from './RuleConfigCompound.vue'
import type { DeliveryFeeRule } from '@/composables/useDeliveryFeeRules'

interface Props {
  show: boolean
  isEditing: boolean
  saving: boolean
  currentRule: DeliveryFeeRule
}

defineProps<Props>()

defineEmits<{
  close: []
  save: []
  'rule-type-change': []
  'add-condition': []
  'remove-condition': [index: number]
}>()
</script>
